import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { updateSEOMeta, resetSEOMeta, useBlogLanguage } from '../utils/seoUtils';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hola%2C%20quiero%20agendar%20una%20consulta%20gratuita%20con%20Marea%20Riviera";

export default function BlogPuertoMorelos() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  useBlogLanguage('es');

  useEffect(() => {
    updateSEOMeta({
      title: es ? 'Invertir en Puerto Morelos 2026: Guía Completa | Marea Riviera' : 'Investing in Puerto Morelos 2026: Complete Guide | Marea Riviera',
      description: es ? 'Puerto Morelos ofrece precios 30-40% más bajos que Playa del Carmen, plusvalía del 12% anual y ROI de 6-10%. Descubre por qué es la mejor inversión de la Riviera Maya en 2026.' : 'Puerto Morelos offers prices 30-40% lower than Playa del Carmen, 12% annual appreciation and 6-10% ROI. Discover why it\'s the best Riviera Maya investment in 2026.',
      url: 'https://mareariviera.com/blog/invertir-puerto-morelos-riviera-maya',
      lang: es ? 'es' : 'en'
    });
    window.scrollTo(0, 0);
    return () => { resetSEOMeta(); };
  }, [es]);

  const scrollToContact = () => { sessionStorage.setItem('scrollTo', 'contact'); navigate('/'); };

  return (
    <div className="bg-[#FAFAF7] min-h-screen pt-[120px]">
      <div className="bg-[#0A2235] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#2BBFBF] hover:text-[#7ECFCF] transition-colors mb-8 font-inter text-sm" data-testid="blog-back-btn">
            <ArrowLeft className="w-4 h-4" /> {es ? 'Volver al inicio' : 'Back to home'}
          </button>
          <div className="flex items-center gap-4 text-[#FAFAF7]/50 font-inter text-sm mb-6">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {es ? '19 junio 2026' : 'June 19, 2026'}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {es ? '12 min lectura' : '12 min read'}</span>
          </div>
          <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] leading-tight mb-6" data-testid="blog-title">
            {es ? 'Puerto Morelos: La Joya Escondida de la Riviera Maya para Invertir en 2026' : 'Puerto Morelos: The Hidden Gem of the Riviera Maya for Investing in 2026'}
          </h1>
          <p className="font-inter text-[#FAFAF7]/70 text-lg leading-relaxed">
            {es ? 'Precios 30-40% más bajos que Playa del Carmen, plusvalía del 12% anual y ROI de 6-10%.' : 'Prices 30-40% lower than Playa del Carmen, 12% annual appreciation and 6-10% ROI.'}
          </p>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="blog-content">
        <div className="prose-marea">
          <p>Hay un lugar en la Riviera Maya que los inversionistas más informados llevan años mirando — y que la mayoría todavía no conoce.</p>
          <p><strong>Puerto Morelos.</strong> A 20 minutos del aeropuerto de Cancún, entre la saturación de Cancún y el boom de Playa del Carmen, existe un pueblo que combina lo que pocos destinos pueden ofrecer: precios accesibles, crecimiento sostenido, y una calidad de vida que Tulum ya no puede garantizar.</p>
          <p>Este artículo explica por qué Puerto Morelos es la apuesta más inteligente de la Riviera Maya en 2026 — con datos reales.</p>

          <h2>¿Qué Es Puerto Morelos y Por Qué Importa?</h2>
          <p>Puerto Morelos es un municipio costero ubicado entre Cancún y Playa del Carmen, sobre la autopista federal 307. Durante décadas fue un pueblo de pescadores prácticamente ignorado por el turismo masivo. Hoy es uno de los mercados inmobiliarios de mayor crecimiento en todo México.</p>
          <p><strong>Algunos datos que explican por qué:</strong></p>
          <ul>
            <li><strong>Crecimiento poblacional del 32%</strong> en la última década — impulsado por residentes, expatriados y nómadas digitales que huyen del caos de Cancún y los precios de Playa del Carmen</li>
            <li><strong>Aumento del 30% en transacciones inmobiliarias en 2023</strong> — uno de los crecimientos más altos de la Riviera Maya según el INEGI</li>
            <li><strong>Estación del Tren Maya</strong> — conecta directamente con Cancún, Playa del Carmen y Tulum</li>
            <li><strong>20 minutos del Aeropuerto Internacional de Cancún</strong> — acceso inmediato para turistas internacionales sin pasar por la ciudad</li>
          </ul>

          <h2>La Ventaja Competitiva: Precios 30-40% Más Bajos</h2>
          <p>Este es el argumento más poderoso de Puerto Morelos para el inversionista inteligente.</p>
          <p>Mientras en Playa del Carmen el metro cuadrado promedia $2,500–$4,000 USD y en Tulum supera los $3,500 USD, en Puerto Morelos puedes encontrar propiedades de calidad comparable entre <strong>$1,800 y $3,200 USD por metro cuadrado</strong>.</p>
          <p>Eso significa que con el mismo capital puedes:</p>
          <ul>
            <li>Comprar una propiedad de mayor tamaño</li>
            <li>Entrar al mercado con menor inversión inicial</li>
            <li>Diversificar en dos propiedades en lugar de una</li>
          </ul>

          <h3>Precios de entrada actuales en Puerto Morelos (2026)</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Tipo de propiedad</th><th>Precio desde</th></tr></thead>
              <tbody>
                <tr><td>Studio / 1 recámara</td><td>$150,000 – $220,000 USD</td></tr>
                <tr><td>2 recámaras</td><td>$220,000 – $380,000 USD</td></tr>
                <tr><td>Beachfront premium</td><td>$300,000 – $600,000 USD</td></tr>
                <tr><td>Penthouse / 3+ recámaras</td><td>$380,000 – $1,000,000 USD</td></tr>
              </tbody>
            </table>
          </div>
          <p><em>Fuente: Caribe Luxury Homes, Coastal Homes Riviera Maya — datos mayo 2026</em></p>

          <h2>Rendimientos Reales: ¿Cuánto Genera una Propiedad en Puerto Morelos?</h2>

          <h3>Renta vacacional</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Métrica</th><th>Dato</th></tr></thead>
              <tbody>
                <tr><td>Tarifa promedio por noche</td><td>$140 – $150 USD</td></tr>
                <tr><td>Ocupación anual estimada</td><td>36% – 45% (promedio mercado)</td></tr>
                <tr><td>Ocupación beachfront bien operado</td><td>60% – 80%</td></tr>
                <tr><td>ROI bruto anual</td><td>6% – 10%</td></tr>
                <tr><td>Plusvalía histórica anual</td><td>12% anual (últimos 5 años)</td></tr>
              </tbody>
            </table>
          </div>
          <p><em>Fuente: AirDNA, Coastal Homes Riviera Maya, Gran Vía del Mar — 2025-2026</em></p>

          <h3>Comparativa con otros destinos de la Riviera Maya</h3>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Destino</th><th>Precio promedio m²</th><th>ROI bruto</th><th>Plusvalía anual</th></tr></thead>
              <tbody>
                <tr><td>Tulum (Aldea Zamá)</td><td>$3,500 – $6,000 USD</td><td>10–15%</td><td>10–15%</td></tr>
                <tr><td>Playa del Carmen</td><td>$2,500 – $4,500 USD</td><td>8–12%</td><td>8–10%</td></tr>
                <tr><td><strong>Puerto Morelos</strong></td><td><strong>$1,800 – $3,200 USD</strong></td><td><strong>6–10%</strong></td><td><strong>12% anual</strong></td></tr>
                <tr><td>Cancún (Zona Hotelera)</td><td>$2,500 – $5,000 USD</td><td>8–13%</td><td>7–9%</td></tr>
              </tbody>
            </table>
          </div>
          <p>Puerto Morelos no tiene el ROI bruto más alto de la región — pero tiene la <strong>mejor relación precio-plusvalía</strong>. Estás comprando a precio de ayer en un mercado que está valorando a ritmo acelerado.</p>

          <h2>Lo Que Hace Único a Puerto Morelos</h2>

          <h3>1. Arrecife protegido — un activo natural irreproducible</h3>
          <p>Puerto Morelos alberga parte del <strong>Arrecife Mesoamericano</strong>, el segundo arrecife de coral más grande del mundo. Esto tiene dos implicaciones importantes para el inversionista:</p>
          <ul>
            <li><strong>Límite natural de construcción</strong> — las regulaciones ambientales restringen el desarrollo frente al arrecife, lo que protege la oferta y sostiene los precios</li>
            <li><strong>Atractivo diferenciado</strong> — snorkel, buceo y naturaleza auténtica atraen un perfil de turista diferente al de Cancún o Tulum, con mayor permanencia y gasto</li>
          </ul>

          <h3>2. Mercado menos saturado que Tulum</h3>
          <p>Mientras Tulum tiene más de 13,000 propiedades activas en Airbnb compitiendo entre sí, Puerto Morelos tiene un inventario significativamente menor. Menos oferta con demanda creciente = mejores ocupaciones para los primeros en entrar.</p>

          <h3>3. Comunidad de expatriados consolidada</h3>
          <p>Puerto Morelos tiene una de las comunidades de expatriados más establecidas de la Riviera Maya — estadounidenses, canadienses y europeos que eligieron el pueblo por su autenticidad y tranquilidad. Esto genera demanda de <strong>renta de largo plazo</strong> (3-12 meses) además de la vacacional, diversificando las fuentes de ingreso.</p>

          <h3>4. Infraestructura completa sin el caos urbano</h3>
          <p>A diferencia de Tulum, Puerto Morelos tiene servicios urbanos completos: agua potable, drenaje, servicios médicos, supermercados, colegios internacionales — y a 20 minutos del hospital más completo de la región en Cancún. Sin el tráfico, el ruido ni la masificación de las zonas más turísticas.</p>

          <h2>¿Para Quién Es Puerto Morelos?</h2>

          <h3>El perfil ideal del inversionista en Puerto Morelos</h3>
          <p><strong>Puerto Morelos es para ti si:</strong></p>
          <ul>
            <li>Buscas <strong>precio de entrada más accesible</strong> sin sacrificar calidad</li>
            <li>Quieres <strong>plusvalía a mediano plazo</strong> — estás comprando antes de que el mercado madure completamente</li>
            <li>Prefieres un mercado <strong>menos competido</strong> en renta vacacional</li>
            <li>Valoras la opción de <strong>uso personal + renta</strong> — vivir temporadas y rentar el resto del año</li>
            <li>Es tu <strong>primera inversión</strong> en la Riviera Maya y quieres minimizar riesgo</li>
          </ul>
          <p><strong>Puerto Morelos puede no ser ideal si:</strong></p>
          <ul>
            <li>Buscas el ROI bruto más alto posible en el corto plazo (ahí Tulum sigue siendo el rey)</li>
            <li>Necesitas la mayor liquidez posible (mercado más pequeño = menos compradores potenciales al revender)</li>
          </ul>

          <h2>Zonas y Desarrollos a Considerar en 2026</h2>

          <h3>Centro / Pueblo</h3>
          <p>La zona más consolidada. Acceso a todos los servicios, ambiente de pueblo auténtico. Mejor para renta de largo plazo y uso personal.</p>

          <h3>Zona Hotelera Norte</h3>
          <p>Más cercana a Cancún. Desarrollos de mayor escala con amenidades completas. Mejor relación precio-amenidades del mercado.</p>

          <h3>Beachfront / Frente al Mar</h3>
          <p>Inventario muy limitado. Las propiedades frente al arrecife tienen restricciones de construcción que protegen su valor. Mayor ticket de entrada pero también mayor demanda y tarifas de renta.</p>

          <h3>Desarrollos destacados activos en 2026</h3>
          <ul>
            <li><strong>Sole Blu</strong> — beachfront premium, desde $545,000 USD, ya 65% vendido, entrega verano 2026</li>
            <li><strong>Distrito Morelos</strong> — beach-edge, ~30 unidades, rooftop pool</li>
            <li><strong>BAO</strong> — desde studios hasta penthouses frente al mar</li>
          </ul>

          <h2>Puerto Morelos vs. Tulum vs. Playa del Carmen: ¿Cuál Elegir?</h2>
          <div className="overflow-x-auto my-8">
            <table>
              <thead><tr><th>Factor</th><th>Puerto Morelos</th><th>Tulum</th><th>Playa del Carmen</th></tr></thead>
              <tbody>
                <tr><td>Precio de entrada</td><td className="text-[#2BBFBF] font-medium">Más accesible</td><td>Alto</td><td>Medio</td></tr>
                <tr><td>ROI bruto</td><td>6-10%</td><td className="text-[#2BBFBF] font-medium">10-15%</td><td>8-12%</td></tr>
                <tr><td>Plusvalía</td><td className="text-[#2BBFBF] font-medium">12%/año</td><td className="text-[#2BBFBF] font-medium">Alta</td><td>8-10%</td></tr>
                <tr><td>Saturación Airbnb</td><td className="text-[#2BBFBF] font-medium">Baja</td><td>Alta</td><td>Media</td></tr>
                <tr><td>Estabilidad mercado</td><td className="text-[#2BBFBF] font-medium">Alta</td><td>Media</td><td className="text-[#2BBFBF] font-medium">Alta</td></tr>
                <tr><td>Uso personal</td><td className="text-[#2BBFBF] font-medium">Ideal</td><td>Ok</td><td>Ok</td></tr>
                <tr><td>Liquidez al revender</td><td>Media</td><td className="text-[#2BBFBF] font-medium">Alta</td><td className="text-[#2BBFBF] font-medium">Alta</td></tr>
              </tbody>
            </table>
          </div>
          <p>No hay un ganador absoluto — hay el destino correcto para tu perfil. Puerto Morelos gana en precio de entrada, plusvalía y calidad de vida. Tulum gana en ROI bruto. Playa del Carmen gana en liquidez y estabilidad.</p>

          <h2>El Momento de Entrar en Puerto Morelos Es Ahora</h2>
          <p>Los inversionistas que compraron en Tulum hace 5 años pagaron $1,500–$2,000 USD por metro cuadrado. Hoy ese mismo metro cuadrado vale $4,000–$6,000 USD.</p>
          <p>Puerto Morelos está hoy donde Tulum estaba hace 5 años — con la diferencia de que tiene menos riesgo de sobreoferta, mejor infraestructura urbana, y un perfil de comprador más diversificado (turistas, residentes, nómadas digitales, expatriados).</p>
          <p>La pregunta no es si Puerto Morelos va a crecer. La pregunta es si vas a estar dentro cuando lo haga.</p>
        </div>

        <div className="mt-16 bg-[#0A2235] rounded-[20px] p-8 md:p-12 text-center">
          <h2 className="font-playfair text-2xl md:text-3xl text-[#FAFAF7] mb-4">
            {es ? '¿Quieres Analizar Opciones en Puerto Morelos?' : 'Want to Analyze Options in Puerto Morelos?'}
          </h2>
          <p className="font-inter text-[#FAFAF7]/70 mb-8 max-w-xl mx-auto leading-relaxed">
            {es ? 'En Marea Riviera acompañamos inversionistas en Tulum, Playa del Carmen y Puerto Morelos — con due diligence, proyecciones reales de ROI y soporte durante todo el proceso.' : 'At Marea Riviera we support investors in Tulum, Playa del Carmen, and Puerto Morelos — with due diligence, real ROI projections, and support throughout the buying process.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full px-8 py-6 text-base" data-testid="blog-cta-whatsapp">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">{es ? 'Agenda una consulta gratuita' : 'Schedule a free consultation'}</a>
            </Button>
            <Button onClick={scrollToContact} variant="outline" className="border-[#2BBFBF] text-[#2BBFBF] hover:bg-[#2BBFBF] hover:text-[#0A2235] rounded-full px-8 py-6 text-base" data-testid="blog-cta-contact">
              {es ? 'Ir al formulario de contacto' : 'Go to contact form'}
            </Button>
          </div>
          <p className="font-inter text-[#FAFAF7]/40 text-sm mt-6">{es ? 'Sin compromiso. Respuesta en menos de 2 horas.' : 'No commitment. Response within 2 hours.'}</p>
        </div>

        <div className="mt-12 pt-8 border-t border-[#0A2235]/10">
          <p className="font-inter text-[#0A2235]/50 text-sm italic leading-relaxed">
            Datos basados en: Caribe Luxury Homes (mayo 2026), Coastal Homes Riviera Maya, Gran Vía del Mar, TheLatinvestor, INEGI. Marea Riviera es una brokerage de inversión inmobiliaria especializada en Tulum, Playa del Carmen y Puerto Morelos.
          </p>
        </div>
      </article>
    </div>
  );
}
